import { useState, useEffect } from 'react';
import { useQuery } from "@tanstack/react-query";
import TeacherCodeModal from './TeacherCodeModal';

const MissionDetailPage = ({ passport, explore }) => {
	const API_URL = import.meta.env.VITE_API_URL;
	const [isModalOpen, setIsModalOpen] = useState(false);
	const [form, setForm] = useState({explore: explore, exploreName: "", type: "", id: ""});

	const { data : exploreData } = useQuery({
		queryKey: ['explore', passport?.id, explore?.code, explore?.level],
		queryFn: async () => {
			const params = new URLSearchParams();
			params.append("passportId", passport?.id);
			params.append("exploreLevel", explore?.level);
			params.append("exploreCode", explore?.code);

			const response = await fetch(`${API_URL}/bbs/ajax.get_explore.php`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded',
				},
				body: params,
				credentials: 'include'
			});

			if (!response.ok) throw new Error("네트워크 오류");

			return response.json();
		},
		enabled: !!passport?.id,
	});

	const exploreItems = exploreData?.data?.list ?? null;

	const { data : passportResultData } = useQuery({
		queryKey: ['passportResult', explore.code, explore.level],
		queryFn: async () => {
			const params = new URLSearchParams();
			params.append("passportId", passport?.id);
			params.append("exploreLevel", explore?.level);
			params.append("exploreCode", explore?.code);

			const response = await fetch(`${API_URL}/bbs/ajax.get_passport_result.php`, {
				method: 'POST',
				headers: {
					'Content-Type': 'application/x-www-form-urlencoded',
				},
				body: params,
				credentials: 'include'
			});
			
			if (!response.ok) throw new Error("네트워크 오류");

			return response.json();
		}
	});
	
	const passportResultItems = passportResultData?.data?.list ?? null;

	const missions = {
        IMG : {1:'🎨',2:'🖼️',3:'🎭'},
        VID : {1:'🎬',2:'🎥',3:'📹'},
        STY : {1:'📖',2:'📚',3:'📜'},
        MUS : {1:'🎵',2:'🎼',3:'🎹'},
        WRT : {1:'✍️',2:'🖊️',3:'✒️'},
        MTH : {1:'🔢',2:'➗',3:'∑'},
        WND : {1:'🔬',2:'🧪',3:'⚗️'},
    };

    const badges = [
        {id: 'speed', icon:'🏃', name:'스피드러너', desc:'한 달 안에 한 나라 완주'},
        {id: 'perfect', icon:'🎯', name:'완벽주의자', desc:'모든 프로젝트 100점'},
        {id: 'presenter', icon:'🎤', name:'발표왕', desc:'프로젝트 10번 발표'},
        {id: 'passion', icon:'🔥', name:'열정왕', desc:'3일 연속 미션 완수'}
    ];

    const handleClick = (type, id) => {
    	setForm(prev => ({...prev, type: type, id: id}));
    	setIsModalOpen(true);
    }

    useEffect(() => {
    	if (exploreItems?.[0]?.name) {
    		setForm(prev => ({...prev, exploreName: exploreItems[0].name}));
    	}
    }, [exploreItems]);

	return (
		<div className="bg-transparent md:bg-slate-100 p-2 min-h-screen flex justify-center items-start">
			<div className="relative w-full bg-white p-[30px] m-[20px] mb-[80px] md:mb-[20px] border-[3px] border-dashed border-slate-400 rounded-[15px] before:content-[''] before:absolute before:w-[30px] before:h-[30px] before:bg-slate-100 before:rounded-full before:border-[3px] before:border-slate-400 before:top-1/2 before:-translate-y-1/2 before:-left-[18px] after:content-[''] after:absolute after:w-[30px] after:h-[30px] after:bg-slate-100 after:rounded-full after:border-[3px] after:border-slate-400 after:top-1/2 after:-translate-y-1/2 after:-right-[18px]">
				<div className="flex justify-between items-center mb-5 pb-5 border-b-[3px] border-dashed border-slate-200">
					<div>
						<div className="text-[48px] font-[900] text-blue-800 leading-none">{missions[explore.code]?.[explore.level] ?? '❓'}</div>
						<div className="text-[14px] text-slate-500 font-bold mt-2">{explore.code}</div>
					</div>
					<div className="hidden md:block text-[36px] text-slate-400">✈️</div>
					<div className="text-right">
						<div className="text-[24px] font-bold text-blue-900">{form.exploreName}</div>
					</div>
					{passportResultItems?.filter(item => item.type === 'mission' && item.isDone)?.length >= 5 && (
						<div className="absolute top-5 right-5 w-[120px] h-[120px] border-4 border-emerald-600 rounded-full bg-emerald-600/10 text-emerald-600 flex flex-col items-center justify-center font-bold -rotate-[15deg] z-10 animate-[stampPop_0.5s_ease]">
							<div className="text-3xl mb-1">✓</div>
							<div className="text-sm">APPROVED</div>
							<div className="text-xs">
								{passportResultItems
									?.filter(item => item.type === 'mission' && item.isDone)
									?.map(v => v.datetime)
									?.sort((a, b) => new Date(b) - new Date(a))?.[0]
									?.split(' ')[0]
									?.split('-')
									?.join('. ')
								}
							</div>
						</div>
					)}
				</div>
				<div className="mb-5">
					<div className="flex justify-between text-[12px] mb-2 font-bold">
						<span>미션 스탬프</span>
						<span className="text-red-500 font-bold">{passportResultItems?.filter(item => item.type === 'mission' && item.isDone)?.length || 0} / {exploreData?.data?.pagination?.total_count}</span>
					</div>
					<div className="grid grid-cols-5 gap-[10px] my-5">
						{exploreItems?.map((item, index) => {
							const missionIsDone = index < (passportResultItems?.filter(v => v.type === 'mission' && v.isDone)?.length || 0);
							return (
								<div
									key={item.id}
									className={`min-h-[60px] md:h-24 px-3 py-2 rounded-lg flex items-center justify-center text-xl transition-all
									${missionIsDone
										? 'bg-gradient-to-br from-yellow-100 to-yellow-300 border-2 border-yellow-500 shadow-md'
										: 'bg-gray-50 border-2 border-dashed border-gray-200'}
										`}
									>
									{missionIsDone ? '⭐' : ''}
								</div>
          					);
						})}
					</div>
				</div>
				<div className="max-h-[280px] overflow-y-auto mb-5 space-y-[10px]">
					{exploreItems?.map((item, index) => {
						const missionIsDone = passportResultItems?.some(v => v.type === 'mission' && v.isDone && v.id === item.id);
						return (
							<div
								key={item.id}
								className={`p-[10px] rounded-lg flex justify-between items-center shadow-sm transition-all
									${missionIsDone ? 'bg-green-50 border-2 border-green-500' : 'bg-gray-50 border-2 border-gray-200'}
								`}
					        >
					        	<div className="flex-1">
					        		<div className="font-bold text-[13px] text-gray-800 text-left">
					        			{index + 1}. {item.mission}
					        		</div>
					        	</div>
					        	<div className="ml-2">
					        		{missionIsDone ? (
					        			<span className="text-green-500 text-[12px] font-bold">
					        				✓ 완료
					        			</span>
					        			) : (
					        			<button
					        				onClick={() => handleClick("mission", item.id)}
					        				className="bg-blue-500 hover:bg-blue-600 text-white px-[14px] py-[6px] rounded-md text-[11px] font-bold transition-colors"
					        			>
					        				스탬프 받기
					        			</button>
					        		)}
					        	</div>
					        </div>
						);
					})}
				</div>
				<div className="p-5 bg-gradient-to-br from-amber-50 to-yellow-200 rounded-xl border-2 border-amber-500">
					<div className="font-bold mb-3 text-amber-900 text-[14px]">🏅 {form.exploreName} 특별 배지</div>
					<div className="grid grid-cols-2 gap-2">
						{badges?.map((item) => {
							const badgeIsDone = passportResultItems?.some(v => v.type === 'badge' && v.isDone && v.id === item.id);
							return (
								<div
									key={item.id}
									onClick={() => handleClick("badge", item.id)}
									className={`p-[10px] rounded-lg cursor-pointer transition-all duration-300 text-center border-2
										${badgeIsDone ? 'bg-gradient-to-br from-emerald-100 to-emerald-300 border-emerald-500' : 'bg-white border-gray-300 hover:border-gray-400'}
						        	`}
						        >
						        	<div className="text-[28px] mb-[4px]">
						        		{item.icon}
						        	</div>
						        	<div
						        		className={`text-[10px] font-bold
						        		    ${badgeIsDone ? 'text-emerald-800' : 'text-gray-500'}
						        		`}
						        	>
						        		{item.name}
						       		</div>
						       	</div>
						    );
						})}
					</div>
					<div className="mt-3 text-center text-[11px] font-bold text-amber-900">
						획득한 배지: {passportResultItems?.filter(v => v.type === 'badge' && v.isDone)?.length || 0} / {badges.length}
					</div>
				</div>
			</div>
			{isModalOpen && (
				<TeacherCodeModal 
					form={form}
					onClose={() => setIsModalOpen(false)} 
				/>
			)}
		</div>
	);
}

export default MissionDetailPage;